import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import gradesEmptyImg from '@/assets/images/dashboard-tiles/1.webp';
import { countUnread } from '@/components/Dashboard/countUnread';
import { GradesModal } from '@/components/Dashboard/GradesModal';
import { useEvaluationModal } from '@/components/EvaluationModal/useEvaluationModal';
import { GradesIcon } from '@/components/Icons/DashboardIcons';
import { DashboardTile } from '@/components/Dashboard/DashboardTile';
import { EmptyTileState } from '@/components/Dashboard/EmptyTileState';
import { TileLoadingState } from '@/components/Dashboard/TileLoadingState';
import { TileSubscriptionPaywall } from '@/components/Dashboard/TileSubscriptionPaywall';
import { UnreadDot } from '@/components/Dashboard/UnreadDot';
import { getEvaluationLabel } from '@/components/Dashboard/evaluationLabel';
import { getLessonTypeChipClass } from '@/components/Dashboard/lessonTypeChipClass';
import { LessonTypeChip } from '@/components/LessonTypeChip';
import { DashboardTileType } from '@/types/components';
import { useEvaluationsSummary } from '@/components/Dashboard/hooks/useEvaluationsSummary';
import { RatingChip, ScaleChip } from '@/components/Icons/MarkChipIcons';
import { useHasFamilySubscription } from '@/hooks/useHasFamilySubscription';
import {
  formatShortDate,
  isToday,
  isYesterday,
  sortByDateDesc,
} from '@/utils/date';

const GRADES_LIMIT = 4;

export const GradesTile: React.FC = () => {
  const { t } = useTranslation();
  const hasSubscription = useHasFamilySubscription();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { onOpen: onOpenEvaluation } = useEvaluationModal();
  const { data, isLoading } = useEvaluationsSummary({
    enabled: hasSubscription,
  });
  const allEvaluations = data?.evaluations ?? [];
  const evaluations = sortByDateDesc(
    allEvaluations,
    ({ timeCreated }) => timeCreated,
  ).slice(0, GRADES_LIMIT);
  const isEmpty = evaluations.length === 0;
  const hasOlderGrades = allEvaluations.length > GRADES_LIMIT;
  const badgeCount = hasSubscription ? countUnread(allEvaluations) : 0;

  return (
    <>
      <DashboardTile
        title={t('home.dashboard.grades.title')}
        footerButtonLabel={t('home.dashboard.grades.viewOlder')}
        type={DashboardTileType.Grades}
        icon={GradesIcon}
        badgeCount={badgeCount}
        hideFooter={!hasSubscription || isLoading || !hasOlderGrades}
        isCompact={!hasSubscription}
        onFooterButtonClick={() => setIsModalOpen(true)}
      >
        {!hasSubscription && (
          <TileSubscriptionPaywall
            imageSrc={gradesEmptyImg}
            description={t('home.dashboard.grades.subscriptionEmpty')}
          />
        )}
        {hasSubscription && (
          <>
            {isLoading && <TileLoadingState />}
            {!isLoading && isEmpty && <EmptyTileState />}
            {!isLoading && !isEmpty && (
              <ul className="flex flex-col">
                {evaluations.map((evaluation) => {
                  const chipClass = getLessonTypeChipClass(
                    evaluation.lesson.type.lessonTypeId,
                  );

                  return (
                    <li
                      key={evaluation.id}
                      className="flex cursor-pointer flex-col gap-1 border-b border-border px-4 py-3 transition-colors last:border-b-0 hover:bg-hover"
                      onClick={() => onOpenEvaluation(evaluation.id)}
                    >
                      <div className="mb-1 flex items-center justify-between gap-2">
                        <span className="text-subhead-14 truncate text-foreground">
                          {evaluation.disciplineName}
                        </span>
                        <span className="text-body-13 md:text-body-14 flex shrink-0 items-center gap-1 text-subtle-foreground">
                          {isToday(evaluation.lesson.date) &&
                            `${t('home.dashboard.absences.today')}, `}
                          {isYesterday(evaluation.lesson.date) &&
                            `${t('home.dashboard.absences.yesterday')}, `}
                          {formatShortDate(evaluation.lesson.date)}
                          {evaluation.isNew && <UnreadDot />}
                        </span>
                      </div>
                      <div className="flex items-center justify-between gap-3">
                        <LessonTypeChip
                          name={evaluation.lesson.type.name}
                          lessonTypeId={evaluation.lesson.type.lessonTypeId}
                        />
                        <div className="flex shrink-0 items-center gap-1">
                          {evaluation.value && (
                            <span
                              className={`text-body-13 md:font-normal flex h-6 min-w-6 shrink-0 items-center justify-center rounded-xl px-2 font-medium tracking-[0.025em] text-foreground ${chipClass}`}
                            >
                              {getEvaluationLabel(evaluation)}
                            </span>
                          )}
                          {evaluation.isWeighted && <ScaleChip />}
                          {evaluation.lesson.isTest && <RatingChip />}
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </>
        )}
      </DashboardTile>
      {isModalOpen && (
        <GradesModal isOpen onClose={() => setIsModalOpen(false)} />
      )}
    </>
  );
};
